import React from 'react';
import styled from 'styled-components';
import { IoIosClose } from 'react-icons/io';
import palette from '../../lib/styles/palette';
import categoryMap from '../../lib/def/category';
import { inputNumberAutoComma } from '../../lib/commonFunc/commonFunc';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

const FullScreen = styled.div`
  position: fixed;
  z-index: 30;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBlock = styled.div`
  width: 520px;
  max-height: 85vh;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media screen and (max-width: 720px) {
    width: 95%;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const DateText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #222;
  font-family: 'Noto Sans KR', sans-serif;
`;

const ArrowBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  margin: 0 12px;
  transition: background 0.15s;

  &:hover {
    background: #f2f2f2;
  }

  svg {
    font-size: 24px;
    color: #555;
  }
`;

const CloseBtn = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: #f2f2f2;
  }

  svg {
    font-size: 28px;
    color: #888;
  }
`;

const ModalBody = styled.div`
  padding: 16px 24px 24px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const CategoryBlock = styled.div`
  & + & {
    margin-top: 20px;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f7f8fa;
  border-radius: 8px;
  border-left: 3px solid ${palette.webCyan[5]};
  margin-bottom: 4px;
`;

const CategoryTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #333;
  font-family: 'Noto Sans KR', sans-serif;
`;

const CategorySub = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #999;
  font-family: 'Noto Sans KR', sans-serif;
`;

const ItemList = styled.div`
  padding: 8px 0;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 6px;
  transition: background 0.1s;

  &:hover {
    background: #fafafa;
  }
`;

const ItemName = styled.div`
  flex: 3;
  font-size: 14px;
  color: #333;
  font-family: 'Noto Sans KR', sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const ItemCount = styled.div`
  flex: 1;
  font-size: 13px;
  color: #888;
  text-align: right;
  font-family: 'Noto Sans KR', sans-serif;
`;

const ItemPrice = styled.div`
  flex: 2;
  font-size: 14px;
  font-weight: 600;
  color: #222;
  text-align: right;
  font-family: 'Noto Sans KR', sans-serif;
`;

const CategoryTotal = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 14px;
  border-top: 1px solid #f0f0f0;
`;

const TotalText = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #222;
  font-family: 'Noto Sans KR', sans-serif;
`;

const DailySalesModal = ({
  visible,
  onClose,
  memberships,
  selectedDay,
  items,
  onClickDateArrow,
  goods,
}) => {
  if (!visible) return null;

  const registrationStatus = (category) => {
    let initState = { new: 0, old: 0, newbeeSum: 0, oldbeeSum: 0 };
    if (!memberships[category]) return initState;

    const result = memberships[category].reduce((count, m) => {
      if (m.isNew === 1)
        return {
          ...count,
          new: (count.new += 1),
          newbeeSum: (count.newbeeSum += m.price),
        };
      else
        return {
          ...count,
          old: (count.old += 1),
          oldbeeSum: (count.oldbeeSum += m.price),
        };
    }, initState);
    return result;
  };

  const count = (category, item) => {
    if (!memberships[category]) return 0;

    return memberships[category].reduce((count, m) => {
      if (item.id === m.clubItemID) return (count += 1);
      return count;
    }, 0);
  };

  const goodsCount = (category, item) => {
    if (!goods[category]) return 0;

    let clickedDay = new Date(selectedDay);

    return goods[category].filter(item => new Date(item.registTime * 1000).getFullYear() == clickedDay.getFullYear() &&
    new Date(item.registTime * 1000).getMonth() == clickedDay.getMonth() && new Date(item.registTime * 1000).getDate() == clickedDay.getDate()).reduce((purchaseCount, m) => {
      if (item.id === m.clubItemID) return (purchaseCount += m.count);
      return purchaseCount;
    }, 0);
  };

  const income = (category, item) => {
    if (!memberships[category]) return 0;
    return inputNumberAutoComma(
      memberships[category].reduce((total, m) => {
        if (item.id === m.clubItemID) return (total += m.price);
        return total;
      }, 0),
    );
  };

  const totalCount = (category) => {
    if (!memberships[category]) return 0;
    return inputNumberAutoComma(memberships[category].length);
  };

  const totalIncome = (category) => {
    if (!memberships[category]) return 0;
    return inputNumberAutoComma(
      memberships[category].reduce((sum, m) => {
        return (sum += m.price);
      }, 0),
    );
  };

  const lockerTotalCount = () => {
      if(memberships != null && memberships['락커'])
      {
        return memberships['락커'].length;
      }

    return 0;

  }
  const lockerTotalPrice = () => {
      let result = 0;
      if(memberships != null && memberships['락커'])
      {
          for (let i = 0; i < memberships['락커'].length; i++) {
            result += memberships['락커'][i].salePrice;
          }
          return inputNumberAutoComma(result);
      }

    return 0;

  }

  let customItem = new Map()

  for (const key in items) {
    let data = items[key];

    if(memberships.hasOwnProperty(key))
    {
        customItem[key] = items[key];
        continue;
    }

    let isItem = false;
    for (let i = 0; i < data.length; i++) {
        if(data[i].isDelete == 0)
        {
            isItem = true;
            break;
        }
    }

    if(isItem)
    {
        customItem[key] = items[key];
    }
  }

  return (
    <FullScreen>
      <ModalBlock>
        <ModalHeader>
          <ArrowBtn onClick={() => onClickDateArrow(selectedDay, 1)}>
            <MdKeyboardArrowLeft />
          </ArrowBtn>
          <DateText>{selectedDay}</DateText>
          <ArrowBtn onClick={() => onClickDateArrow(selectedDay, -1)}>
            <MdKeyboardArrowRight />
          </ArrowBtn>
          <CloseBtn onClick={() => onClose()}>
            <IoIosClose />
          </CloseBtn>
        </ModalHeader>
        <ModalBody>
          {items &&
            Object.keys(items)
              .sort((a, b) => (categoryMap[a] > categoryMap[b] ? 1 : -1))
              .map((category, idx1) => {
                if (memberships[category] === undefined) return null;
                const regStatus = registrationStatus(category);
                return (
                  <CategoryBlock key={idx1}>
                    <CategoryHeader>
                      <CategoryTitle>{category}</CategoryTitle>
                      {category != "일반" &&
                        <CategorySub>
                          신규 {regStatus.new}명 / 재등록 {regStatus.old}명
                        </CategorySub>
                      }
                    </CategoryHeader>
                    <ItemList>
                      {items[category].map((item, idx2) => {
                        if (item.isDelete === 1 && !memberships[category])
                          return null;
                        if (
                          item.isDelete === 1 &&
                          memberships[category].find(
                            (m) => m.clubItemID === item.id,
                          ) === undefined
                        ) {
                          return null;
                        }

                        if(count(category, item) === 0) return null;

                        return (
                          <ItemRow key={idx2}>
                            <ItemName>{item.title}</ItemName>
                            {category == "일반"
                              ? <ItemCount>{goodsCount(category, item)}개</ItemCount>
                              : <ItemCount>{count(category, item)}명</ItemCount>
                            }
                            <ItemPrice>{income(category, item)}원</ItemPrice>
                          </ItemRow>
                        );
                      })}
                    </ItemList>
                    <CategoryTotal>
                      <TotalText>{totalIncome(category)}원</TotalText>
                    </CategoryTotal>
                  </CategoryBlock>
                );
              })}
              {
                  (memberships['락커'] === undefined)?
                    <></>
                    :
                    <CategoryBlock>
                      <CategoryHeader>
                        <CategoryTitle>락커</CategoryTitle>
                      </CategoryHeader>
                      <ItemList>
                        <ItemRow>
                          <ItemName>락커</ItemName>
                          <ItemCount>{lockerTotalCount()}명</ItemCount>
                          <ItemPrice>{lockerTotalPrice()}원</ItemPrice>
                        </ItemRow>
                      </ItemList>
                      <CategoryTotal>
                        <TotalText>{lockerTotalPrice()}원</TotalText>
                      </CategoryTotal>
                    </CategoryBlock>
              }
        </ModalBody>
      </ModalBlock>
    </FullScreen>
  );
};

export default DailySalesModal;